import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import s from './User.module.scss';
import { FormattedMessage, useIntl } from 'react-intl';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import { initialUserManagerState } from '../../reducers/initialState';
import { v4 as uuidv4 } from 'uuid';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import { Navigate, Link, useParams } from 'react-router-dom';
import { IsWidgetAccessible } from '../../utils/widgetManager';
import UserProfiles from './UserProfiles';
import UserProfileUnits from './UserProfileUnits';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Checkbox from '../WSAControls/CheckboxContainer/Checkbox';
import { NOTIFICATION_DELIVERY_TYPES } from '../../constants/index';
import AdminControlsContentTemplate from '../AdminControls/AdminControlsContentTemplate';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import equal from 'fast-deep-equal';
import classNames from 'classnames';
import { isValidEmail, isRequired } from '../../utils/';
import { v4 as uuid } from 'uuid';
import Cookies from 'js-cookie';

const User = (props) => {
  const intl = useIntl();
  const params = useParams();

  // Environment variables
  const envName = process.env.REACT_APP_ENV_NAME_SHORT;
  const loggedInUserId = Cookies.get(`userid-${envName}`) || 1;
  const organisationId = Cookies.get(`selectedorganisationid-${envName}`) || 1;

  // State
  const [userManager, setUserManager] = useState({
    ...initialUserManagerState.userManager,
    selectedUser: {
      ...initialUserManagerState.userManager.selectedUser,
      selectedProfiles: []
    },
    profiles: [],
    units: []
  });

  const [highlightedProfile, setHighlightedProfile] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [filter, setFilter] = useState(props.unitManager.filter || '');
  const [limit, setLimit] = useState(props.unitManager.limit || 1000);

  // Setup component on mount
  useEffect(() => {
    setupComponent();

    // Cleanup on unmount
    return () => {
      props.setSelectedUser({
        ...initialUserManagerState.userManager.selectedUser,
        selectedProfiles: []
      });
    };
  }, []);

  // Update state when props change
  useEffect(() => {
    if (!equal(props.userManager.selectedUser, userManager.selectedUser)) {
      setUserManager({
        ...userManager,
        selectedUser: { ...props.userManager.selectedUser },
        profiles: props.userManager.profiles,
        units: props.userManager.units
      });
    }
  }, [props.userManager.selectedUser, props.userManager.profiles, props.userManager.units]);

  // Initial setup function
  const setupComponent = () => {
    setIsEdit(false);
    setRedirect(false);
    setFilter(props.unitManager.filter || '');
    setLimit(props.unitManager.limit || 1000);

    let widgetCode = params.userId ? 'USERUPDATE' : 'USERINSERT';
    props.getUnits(limit, filter, organisationId, widgetCode);

    // Checking whether it is edit or add new
    if (params.userId) {
      const userId = params.userId;

      setIsEdit(true);
      // redirecting to list if selected user is empty and userID is not blank
      if (!userId) {
        setRedirect(true);
      } else {
        props.describeUser(userId, widgetCode);
      }
    } else {
      props.describeUser(null, widgetCode);
    }
  };

  // Form submission handler
  const submitForm = (e) => {
    e.preventDefault();

    if (!validateInput()) {
      return;
    }

    let saveData = { ...userManager.selectedUser };
    saveData.IsEdit = isEdit;
    let widgetCode = 'USERINSERT';

    if (!isEdit) {
      saveData.OrganisationId = organisationId;
      saveData.GuidIdentifier = uuidv4();
      saveData.CreatedByUserId = loggedInUserId;
    } else {
      saveData.OrganisationId = organisationId;
      widgetCode = 'USERUPDATE';
      saveData.ModifiedByUserId = loggedInUserId;
    }

    // saving User
    props.saveUser(saveData, widgetCode);
  };

  // Input validation
  const validateInput = () => {
    let newFormErrors = {};
    let isValid = true;

    if (
      !userManager ||
      !userManager.selectedUser ||
      !userManager.selectedUser.FirstName ||
      isRequired(userManager.selectedUser.FirstName, 1)
    ) {
      newFormErrors.FirstName = intl.formatMessage({
        id: 'usermanagment.firstnamemandatory',
        defaultMessage: 'First name is mandatory field'
      });
      isValid = false;
    }

    if (
      !userManager ||
      !userManager.selectedUser ||
      !userManager.selectedUser.Email ||
      !isValidEmail(userManager.selectedUser.Email)
    ) {
      newFormErrors.Email = intl.formatMessage({
        id: 'usermanagment.entervalidemail',
        defaultMessage: 'Please enter valid email'
      });
      isValid = false;
    }

    if (
      !userManager ||
      !userManager.selectedUser ||
      !userManager.selectedUser.PhoneNumber ||
      isRequired(userManager.selectedUser.PhoneNumber.replace(/ /g, ''), 8)
    ) {
      newFormErrors.PhoneNumber = intl.formatMessage({
        id: 'usermanagment.entervalidphoneno',
        defaultMessage: 'Please enter valid phone number (minimum 7 digits including country code)'
      });
      isValid = false;
    }

    if (
      !userManager ||
      !userManager.selectedUser ||
      !userManager.selectedUser.selectedProfiles ||
      userManager.selectedUser.selectedProfiles.length === 0
    ) {
      newFormErrors.Profiles = intl.formatMessage({
        id: 'usermanagment.addatleaseoneprofile',
        defaultMessage: 'Please add at least one profile'
      });
      isValid = false;
    }

    if (
      !userManager ||
      !userManager.selectedUser ||
      !userManager.selectedUser.DeliveryType ||
      isRequired(userManager.selectedUser.DeliveryType, 1)
    ) {
      newFormErrors.DeliveryType = intl.formatMessage({
        id: 'usermanagment.contactNameMandatory',
        defaultMessage: 'Preferred Contact Method is mandatory field'
      });
      isValid = false;
    }

    setFormErrors(newFormErrors);
    return isValid;
  };

  // Handle input change
  const onChange = (e, data) => {
    // Converting phone-value to the required type object
    if (data && data.countryCode) {
      e = { target: { type: 'text', name: 'PhoneNumber', value: e } };
    }

    if (e.target && e.target.type && e.target.type === 'checkbox') {
      setUserManager({
        ...userManager,
        selectedUser: {
          ...userManager.selectedUser,
          [e.target.name]: e.target.checked
        }
      });
    } else {
      setUserManager({
        ...userManager,
        selectedUser: {
          ...userManager.selectedUser,
          [e.target.name]: e.target.value
        }
      });
    }
  };

  // Handle profile selection
  const onSelectedProfileClick = (profile) => {
    setHighlightedProfile(profile);
  };

  // Handle profile list updates
  const onSelectedProfileListUpdated = (profiles) => {
    setUserManager({
      ...userManager,
      selectedUser: {
        ...userManager.selectedUser,
        selectedProfiles: profiles
      }
    });
  };

  // Handle unit selection change
  const onUnitSelectionChange = (selectedProfile) => {
    let updatedProfiles = userManager.selectedUser.selectedProfiles.map(selProf => {
      if (selProf.ProfileId === selectedProfile.ProfileId) {
        return selectedProfile;
      } else {
        return selProf;
      }
    });

    setUserManager({
      ...userManager,
      selectedUser: {
        ...userManager.selectedUser,
        selectedProfiles: updatedProfiles
      }
    });
  };

  // Handle unit search
  const onUnitSearch = (searchFilter) => {
    setFilter(searchFilter);
    let widgetCode = params.userId ? 'USERUPDATE' : 'USERINSERT';
    props.getUnits(limit, searchFilter, organisationId, widgetCode);
  };

  // Get delivery types
  const deliveryTypes = () => {
    let types = [];
    types = Object.keys(NOTIFICATION_DELIVERY_TYPES).map(key => {
      return {
        SKEY: NOTIFICATION_DELIVERY_TYPES[key].value,
        label: NOTIFICATION_DELIVERY_TYPES[key].name,
        value: NOTIFICATION_DELIVERY_TYPES[key].value,
        isChecked:
          userManager.selectedUser.DeliveryType &&
            userManager.selectedUser.DeliveryType.split(',').find(e => e === NOTIFICATION_DELIVERY_TYPES[key].name)
            ? true
            : false
      };
    });
    return types;
  };

  // Handle checkbox selection changes
  const onCheckboxSelectionChange = (e, data) => {
    let deliveryType = userManager.selectedUser.DeliveryType;
    if (data.isChecked) {
      deliveryType = (deliveryType && `${deliveryType},${data.label}`) || data.label;
    } else {
      if (deliveryType) {
        deliveryType = deliveryType
          .split(',')
          .filter(elem => elem !== data.label)
          .join(',');
      }
    }

    setUserManager({
      ...userManager,
      selectedUser: {
        ...userManager.selectedUser,
        DeliveryType: deliveryType
      }
    });
  };

  if ((userManager.selectedUser.IsSupportUser && !IsWidgetAccessible(props.userProfileWidgets, 'USERSUPPORTUSER')) && !(userManager.selectedUser.CreatedOrganisationId === parseInt(organisationId)) ) {
    return <Navigate to="/admin-controls/user-list" />;
  }

  // Redirect if needed
  if (redirect) {
    return <Navigate to="/admin-controls/user-list" />;
  }

  let messageId = (props.userManager && props.userManager.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const placeHolderEmail = intl.formatMessage({
    id: 'usermanagment.enteremail',
    defaultMessage: 'Enter Email'
  });

  let allProfiles = (props.userManager && props.userManager.profiles) || [];

  let userName = (userManager.selectedUser && userManager.selectedUser.Email) || null;

  return (
    <div className={s.user}>
      {props.userManager.isLoading && <LoadingSpinner />}
      <Banner
        key={uuid()}
        failureText={messageText}
        showBanner={props.userManager.showBanner}
        status={props.userManager.isOpSuccessfull}
        successText={messageText}
      />
      {props.userManager.isOpSuccessfull && !props.userManager.showBanner ? <Navigate to="/admin-controls/user-list" /> : ''}

      <AdminControlsContentTemplate selectedPage="manageUsers" userProfileWidgets={props.userProfileWidgets}>
        <div className={s.contentWrapper}>
          <div className={s.userHeader}>
            <Link className={s.backLink} to={'/admin-controls/user-list'}>
              &lt; &nbsp;
              <FormattedMessage id="usermanagement.backtousers" defaultMessage="BACK TO USERS" />
            </Link>
            <h3>
              {isEdit ? (
                <FormattedMessage id="usermanagement.edituserdetails" defaultMessage="Edit user details" />
              ) : (
                <FormattedMessage id="usermanagement.linkaddnewuser" defaultMessage="Add new user" />
              )}
            </h3>
          </div>
          <div className={s.userContent}>
            <Form>
              <Row>
                <Col lg={6}>
                  <Form.Group controlId="formFirstName">
                    <Form.Label>
                      <FormattedMessage id="usermanagement.firstname" defaultMessage="First Name" />
                    </Form.Label>

                    <Form.Control
                      type="text"
                      name="FirstName"
                      onChange={onChange}
                      value={userManager.selectedUser.FirstName}
                      className={`${s.formControl} ${formErrors && formErrors.FirstName ? s.formControlError : ''}`}
                    />
                    {formErrors && formErrors.FirstName && (
                      <p role="alert" className={s.error}>
                        {formErrors.FirstName}
                      </p>
                    )}
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group controlId="formLastName">
                    <Form.Label>
                      <FormattedMessage id="usermanagement.lastname" defaultMessage="Last Name" />
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="LastName"
                      onChange={onChange}
                      value={userManager.selectedUser.LastName}
                      className={s.formControl}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  <Form.Group controlId="formUserEmail">
                    <Form.Label>
                      <FormattedMessage id="usermanagement.email" defaultMessage="Email" />
                    </Form.Label>

                    <Form.Control
                      type="email"
                      name="Email"
                      disabled={isEdit}
                      onChange={onChange}
                      placeholder={placeHolderEmail}
                      value={userManager.selectedUser.Email}
                      className={`${s.formControl} ${formErrors && formErrors.Email ? s.formControlError : ''}`}
                    />
                    {formErrors && formErrors.Email && (
                      <p role="alert" className={s.error}>
                        {formErrors.Email}
                      </p>
                    )}
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group controlId="formUserEmail">
                    <Form.Label>
                      <FormattedMessage id="usermanagement.contactno" defaultMessage="Contact number" />
                    </Form.Label>
                    <PhoneInput
                      country={'au'}
                      inputStyle={{ width: '100%' }}
                      value={userManager.selectedUser.PhoneNumber}
                      onChange={onChange}
                      inputProps={{ name: 'phone' }}
                      inputClass={`${s.formControl} ${formErrors && formErrors.PhoneNumber ? s.formControlError : ''
                        }`}
                      buttonClass={formErrors && formErrors.PhoneNumber ? s.formControlError : ''}
                    />
                    {formErrors && formErrors.PhoneNumber && (
                      <p role="alert" className={s.error}>
                        {formErrors.PhoneNumber}
                      </p>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  <Form.Group controlId="formPreferredContactMethod">
                    <Form.Label>
                      <FormattedMessage id="usermanagement.preferredContactMethod" defaultMessage="Preferred Contact Method" />
                    </Form.Label>
                    <Checkbox dataArray={deliveryTypes()} onSelectionChange={onCheckboxSelectionChange} />
                    {formErrors && formErrors.DeliveryType && (
                      <p role="alert" className={s.error}>
                        {formErrors.DeliveryType}
                      </p>
                    )}
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group controlId="formUserIsEnabled">
                    <Checkbox
                      key={uuidv4()}
                      dataArray={[
                        {
                          SKEY: 'IsActive',
                          target: { type: 'checkbox' },
                          label: 'Enable user',
                          isChecked: userManager.selectedUser.IsActive
                        }
                      ]}
                      onSelectionChange={onChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              {!isEdit && IsWidgetAccessible(props.userProfileWidgets, 'USERSUPPORTUSER') && <Row>
                <Col lg={6}>
                  <Form.Group controlId="formUserIsSupportUser">
                    <Checkbox
                      key={uuidv4()}
                      dataArray={[
                        {
                          SKEY: 'IsSupportUser',
                          target: { type: 'checkbox' },
                          label: 'Is Support user',
                          isChecked: userManager.selectedUser.IsSupportUser
                        }
                      ]}
                      onSelectionChange={onChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              }
            </Form>

            <Form className={s.userProfileUnitsContainer}>
              {/* User Profiles START */}
              <Form.Group controlId="formUserProfiles">
                <Form.Label className={s.subHeading}>
                  <h4>
                    <FormattedMessage id="usermanagement.userprofiles" defaultMessage="Profiles" />
                  </h4>
                </Form.Label>
              </Form.Group>
              <Container className={s.userProfileAndUnitsContainer} fluid={true}>
                <Row>
                  <Col lg={6} className="ps-0">
                    <Form.Label className={s.subHeading2}>
                      <FormattedMessage id="usermanagement.selectprofilethisuser" defaultMessage="1. Select a profile(s) for this user" />
                    </Form.Label>
                  </Col>
                  <Col lg={6} className="d-none d-lg-table-cell ps-0">
                    <Form.Label className={s.subHeading2}>
                      <FormattedMessage id="usermanagement.selectunits" defaultMessage="2. Select a unit(s) for " />
                      {highlightedProfile && highlightedProfile.Name}
                    </Form.Label>
                  </Col>
                </Row>
                <Row className={s.outerRow}>
                  <Col lg={6} className={s.outerLeftCol}>
                    <UserProfiles
                      profiles={allProfiles}
                      selectedUserName={userName}
                      selectedProfiles={userManager.selectedUser.selectedProfiles}
                      onSelectedProfileClick={onSelectedProfileClick}
                      onSelectedProfileListUpdated={onSelectedProfileListUpdated}
                    />
                  </Col>
                  <Col lg={6} className="d-lg-none ps-0">
                    {formErrors && formErrors.Profiles && (
                      <p role="alert" className={s.error}>
                        {formErrors.Profiles}
                      </p>
                    )}
                  </Col>
                  <Col lg={6} className="d-lg-none ps-0">
                    <Form.Label className={s.subHeading2}>
                      <FormattedMessage id="usermanagement.selectunits" defaultMessage="2. Select a unit(s) for " />
                      {highlightedProfile && highlightedProfile.Name}
                    </Form.Label>
                  </Col>
                  <Col lg={6} className={s.outerRightCol}>
                    <UserProfileUnits
                      unitManager={props.unitManager}
                      onUnitSearch={onUnitSearch}
                      highlightedProfile={highlightedProfile}
                      onUnitSelectionChange={onUnitSelectionChange}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col lg={6} className="d-none d-lg-table-cell ps-0">
                    {formErrors && formErrors.Profiles && (
                      <p role="alert" className={s.error}>
                        {formErrors.Profiles}
                      </p>
                    )}
                  </Col>
                </Row>
              </Container>

              {/* User Profiles END */}
              <div>
                <Button variant="primary" className={classNames(s.margin5, s.btnSaveChanges)} onClick={submitForm} noValidate>
                  <FormattedMessage id="profile.profileSave" defaultMessage="Save Changes" />
                </Button>
                <Link to="/admin-controls/user-list">
                  <Button variant="outline-secondary" className={s.btnCancel}>
                    <FormattedMessage id="profile.profileCancel" defaultMessage="Cancel" />
                  </Button>
                </Link>
              </div>
            </Form>
          </div>
        </div>
      </AdminControlsContentTemplate>
    </div>
  );
};



User.propTypes = {
  saveUser: PropTypes.func.isRequired,
  describeUser: PropTypes.func.isRequired,
  setSelectedUser: PropTypes.func.isRequired,
  userManager: PropTypes.object.isRequired,
  unitManager: PropTypes.object.isRequired,
  userProfileWidgets: PropTypes.object.isRequired,
  getUnits: PropTypes.func.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  setPageFilter: PropTypes.func.isRequired
};

export default User;